import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, limparCachePersistente, garantirCacheDoUsuario, esquecerUltimoUid } from '../services/firebase';
import { User, isFullAccess, isScopedManager } from '../types';

interface AuthContextData {
  user: User | null;
  isAdmin: boolean;       // acesso total (admin, diretor, pricing, gerente_nacional)
  isManager: boolean;     // gestor com escopo (gerente, coordenador, supervisor)
  isStaff: boolean;       // qualquer um que não seja vendedor puro (isAdmin || isManager)
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
      if (fbUser) {
        // ANTES de qualquer leitura: se o cache no disco é de OUTRO usuário desta máquina, limpa.
        // Leitura servida pelo cache não passa pelas regras — sem isto, num PC compartilhado o
        // próximo a entrar herdaria a base do anterior. (Recarrega a página quando limpa.)
        await garantirCacheDoUsuario(fbUser.uid);
        const userDoc = await getDoc(doc(db, 'users', fbUser.uid));
        // Admin e vendedor podem acessar o painel web (cada um vê o seu)
        if (userDoc.exists() && userDoc.data().active !== false) {
          setUser({ id: userDoc.id, ...userDoc.data() } as User);
        } else {
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: isFullAccess(user?.role),
        isManager: isScopedManager(user?.role),
        isStaff: isFullAccess(user?.role) || isScopedManager(user?.role),
        loading,
        signIn: (email, password) => signInWithEmailAndPassword(auth, email, password).then(() => {}),
        // Logout LIMPA o cache do disco: sem isso a base seguia legível para o próximo usuário da
        // máquina, sem passar pelas regras. O reload é necessário porque a instância do Firestore
        // precisa ser terminada para que a limpeza seja possível.
        logout: async () => {
          esquecerUltimoUid();
          await signOut(auth);
          await limparCachePersistente();
          window.location.reload();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
